/**
 * Display-side plain-language pass over per-checkpoint `explanation` strings.
 *
 * Why this lives in the frontend rather than only in the evaluator that writes
 * the sentence: evaluation result JSON is PERSISTED (Tier 1, never deleted —
 * docs/DATA_LIFECYCLE.md). A backend copy fix changes only future
 * evaluations; every report a coach already has keeps the old technical
 * wording forever. A narrow rewrite at the render site fixes the copy for the
 * whole history, which is what docs/COACH_FACING_COPY_STANDARD.md asks for.
 *
 * Rules for this module:
 *   - Narrow, anchored patterns only. Anything that does not match is passed
 *     through UNCHANGED — this is not a general-purpose text mangler.
 *   - One entry per owner-approved rewrite in the standard's §3 table.
 *   - Never drop information a coach can act on; the rewrites below replace
 *     a number the coach cannot act on ("within 10 % tolerance") with the
 *     verdict it was standing in for.
 */

/**
 * "Distance matched template within 10% tolerance" (and the mm/absolute
 * variants the evaluator emits from the same format string) →
 * "Distance looks right." — owner-approved, standard §3. Why/how are not
 * needed: there is nothing to do.
 *
 * Source of the original: `spatial_goal_evaluation.py`'s
 * f"Distance matched template within {tolerance_value}{'%'|''} tolerance".
 */
const DISTANCE_WITHIN_TOLERANCE =
  /^distance matched template within\s+[\d.]+%?\s+tolerance\.?$/i;

export function plainGoalExplanation(
  explanation: string | null | undefined,
): string | null | undefined {
  if (!explanation) return explanation;
  const trimmed = explanation.trim();

  if (DISTANCE_WITHIN_TOLERANCE.test(trimmed)) {
    return 'Distance looks right.';
  }

  return explanation;
}
